import React, { useEffect, useState, useCallback, useMemo } from 'react';
import TrackProgress from './ProgressGrid';
import { UserProgress } from '../../types/progress';
import { getUserProgress, getSession, GetSessionResponse, getProgressMetrics, getProgression, ProgressionResponse } from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiPlay, FiBook, FiLock, FiZap, FiSettings } from 'react-icons/fi';
import Logo from '../common/Logo';
import AssessmentAlert from './AssessmentAlert';
import ProgressMetrics from './ProgressMetrics';
import Progression from './Progression';
import TodaysProgress from './TodaysProgress';
import { AxiosError } from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Fact } from '../Learn/types';
import { LoadingProvider, useLoading } from '../../context/LoadingContext';
import TrackCompletionAlert from './TrackCompletionAlert';
import { CQPM_TARGETS, CQPM_TARGETS_BY_TRACK, FLUENCY_TARGETS, TRACK_NAMES, TRACK_LENGTHS } from '../../types/constants';
import { useSession } from '../../context/SessionContext';
import FocusTrackAlert from './FocusTrackAlert';
import UserProfileDropdown from './UserProfileDropdown';

const LoadingOverlay: React.FC = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex flex-col items-center justify-center"
  >
    <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
    <p className="text-lg text-gray-600">Loading...</p>
  </motion.div>
);

const DashboardContent: React.FC = () => {
  const { user } = useAuth();
  const { recordPageTransition } = useSession();
  const [allTracks, setAllTracks] = useState<UserProgress[]>([]);
  const [expandedTrackId, setExpandedTrackId] = useState<string | null>(() => {
    // First check if user has a specific focus track (not ALL)
    if (user?.focusTrack && user.focusTrack !== 'ALL' && Object.keys(TRACK_NAMES).includes(user.focusTrack)) {
      return user.focusTrack;
    }
    
    // If user has focus track "ALL", check sessionStorage first, then default to TRACK5_L2
    if (user?.focusTrack === 'ALL') {
      const storedTrackId = sessionStorage.getItem('activeTrackId');
      if (storedTrackId && Object.keys(TRACK_NAMES).includes(storedTrackId)) {
        return storedTrackId;
      }
      return 'TRACK5_L2';
    }
    
    // Then check if there's a stored track ID in session
    const storedTrackId = sessionStorage.getItem('activeTrackId');
    if (storedTrackId && Object.keys(TRACK_NAMES).includes(storedTrackId)) {
      return storedTrackId;
    }
    
    // Otherwise, set default based on user's grade
    if (user?.ageGrade !== undefined) {
      switch (user.ageGrade) {
        case 0: return 'TRACK12'; // Addition Within 10 for Grade 0 (same as Grade 1)
        case 1: return 'TRACK12'; // Addition Within 10 for Grade 1
        case 2: return 'TRACK9';  // Addition (Single-Digit Addends) for Grade 2
        case 3: return 'TRACK11'; // Multiplication (Single-digit factors) for Grade 3
        case 4: return 'TRACK5';  // Division Facts for Grade 4
        default: return 'TRACK5_L2'; // Default to Division Facts L2 for Grade 5+
      }
    }
    
    // Default to TRACK5_L2 if no grade or other criteria met
    return 'TRACK5_L2';
  });
  const [error, setError] = useState<string | null>(null);
  const [masteredFactsCounts, setMasteredFactsCounts] = useState<Record<string, number>>({});
  const [hasCompletedAssessment, setHasCompletedAssessment] = useState(false);
  const [showCompletionAlert, setShowCompletionAlert] = useState(false);
  const [sessionData, setSessionData] = useState<GetSessionResponse | null>(null);
  const [isSessionLoading, setIsSessionLoading] = useState(false);
  const [targetAchievedComplete, setTargetAchievedComplete] = useState(false);
  const [showFocusTrackAlert, setShowFocusTrackAlert] = useState(false);
  const [completionTriggerType, setCompletionTriggerType] = useState<'facts_mastered' | 'cqpm_achieved'>('facts_mastered');
  const [allTodaysProgressComplete, setAllTodaysProgressComplete] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading, registerLoadingComponent, setComponentLoading } = useLoading();
  const [progressMetricsData, setProgressMetricsData] = useState<any>(null);
  const [progressionData, setProgressionData] = useState<ProgressionResponse | null>(null);

  const markCompletionAlertAsShown = () => {
    setShowCompletionAlert(false);
    setTargetAchievedComplete(false);
  };

  const handleTargetAchieved = () => {
    setTargetAchievedComplete(true);
    setShowCompletionAlert(true);
  };


  // Check if we should show the focus track alert for first-time users
  const checkAndShowFocusTrackAlert = useCallback(() => {
    if (!user?.userId || !user?.focusTrack) return;
    
    // Check if user has seen the welcome alert before
    const alertKey = `focusTrackAlert_${user.userId}`;
    const hasSeenAlert = localStorage.getItem(alertKey);
    
    // Show if they haven't seen it before and have a focus track (including "ALL")
    if (!hasSeenAlert) {
      setShowFocusTrackAlert(true);
    }
  }, [user?.userId, user?.focusTrack]);

  const handleFocusTrackAlertContinue = () => {
    if (user?.userId) {
      // Mark alert as seen for this user
      localStorage.setItem(`focusTrackAlert_${user.userId}`, 'true');
    }
    setShowFocusTrackAlert(false);
  };

  const handleTodaysProgressCompletionChange = (allCompleted: boolean) => {
    setAllTodaysProgressComplete(allCompleted);
  };

  // Handle preserveTrackId from navigation state (e.g., returning from practice activity)
  // Separate effect to avoid triggering a double data-fetch in the main effect
  useEffect(() => {
    if (location.state?.preserveTrackId) {
      const preservedTrackId = location.state.preserveTrackId;

      sessionStorage.setItem('activeTrackId', preservedTrackId);

      if (preservedTrackId !== expandedTrackId) {
        setExpandedTrackId(preservedTrackId);
      }

      // Clear preserveTrackId from location state to prevent re-processing
      navigate(location.pathname, {
        replace: true,
        state: { ...location.state, preserveTrackId: undefined }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.preserveTrackId]);

  // Main data-fetch effect
  useEffect(() => {
    const componentId = 'dashboard-main';
    registerLoadingComponent(componentId);

    // Skip fetching if preserveTrackId is pending — the effect above will update
    // expandedTrackId first, then this effect will re-run with the correct value
    if (location.state?.preserveTrackId) return;

    // Store expandedTrackId in sessionStorage at component mount to ensure it's available for API calls
    if (expandedTrackId) {
      sessionStorage.setItem('activeTrackId', expandedTrackId);
    }

    let isMounted = true;

    const initializeData = async () => {
      if (!user?.userId) {
        setComponentLoading(componentId, false);
        return;
      }

      // Check if user has a focus track, redirect to appropriate flow if not
      if (!user.focusTrack) {
        // Check if user is Pre-K (ageGrade -1)
        if (user.ageGrade === -1) {
          navigate('/pre-k-assessment');
          return;
        } else {
          navigate('/onboarding');
          return;
        }
      }

      // Check if user has a PreK focus track, redirect to appropriate practice
      if (user.focusTrack === 'TRACK_PREK_NAMING') {
        navigate('/pre-k-practice?track=naming');
        return;
      } else if (user.focusTrack === 'TRACK_PREK_SUBITIZING') {
        navigate('/pre-k-practice?track=subitizing');
        return;
      } else if (user.focusTrack === 'TRACK_PREK_COUNTING') {
        navigate('/pre-k-practice?track=counting');
        return;
      } else if (user.focusTrack === 'TRACK_PREK_WHATCOMESNEXT') {
        navigate('/pre-k-practice?track=whatcomesnext');
        return;
      }

      // Check if user has a K focus track, redirect to appropriate practice
      if (user.focusTrack === 'TRACK_K_NAMING') {
        navigate('/k-practice?track=naming');
        return;
      } else if (user.focusTrack === 'TRACK_K_SUBITIZING') {
        navigate('/k-practice?track=subitizing');
        return;
      } else if (user.focusTrack === 'TRACK_K_COMPARING') {
        navigate('/k-practice?track=comparing');
        return;
      } else if (user.focusTrack === 'TRACK_K_PARTWHOLE') {
        navigate('/k-practice?track=partwhole');
        return;
      } else if (user.focusTrack === 'TRACK_K_ADDITION_WITHIN_5') {
        navigate('/k-practice?track=additionwithin5');
        return;
      } else if (user.focusTrack === 'TRACK_K_ACCURACY') {
        navigate('/k-accuracy-assessment');
        return;
      }

      // Check if we should show the focus track alert after confirming user has completed onboarding
      checkAndShowFocusTrackAlert();

      try {
        setIsSessionLoading(true);

        // Fetch all data in parallel — use allSettled so partial failures don't block everything
        const [progressResult, sessionResult, metricsResult, progressionResult] = await Promise.allSettled([
          getUserProgress(user.userId, expandedTrackId || undefined),
          getSession(user.userId, 'all'),
          getProgressMetrics(user.userId),
          getProgression(user.userId)
        ]);

        if (!isMounted) return;

        // Handle each result individually — use whatever succeeded
        if (progressResult.status === 'fulfilled') {
          setAllTracks(progressResult.value.tracks);
        } else {
          console.error('Error fetching user progress:', progressResult.reason);
        }

        if (sessionResult.status === 'fulfilled') {
          const sessionResponse = sessionResult.value;
          setSessionData(sessionResponse);

          // Derive hasCompletedAssessment from session data — no separate API call needed
          setHasCompletedAssessment(
            !!sessionResponse.assessmentStatus?.lastAssessmentDate
          );

          // Auto-navigate to fluency drill if active
          if (sessionResponse?.availableActivities?.fluencyDrill?.active) {
            navigate('/fluency-drill', {
              state: { fluencyData: sessionResponse.availableActivities.fluencyPractice }
            });
            return;
          }
        } else {
          console.error('Error fetching session data:', sessionResult.reason);
        }

        if (metricsResult.status === 'fulfilled') {
          setProgressMetricsData(metricsResult.value);
        } else {
          console.error('Error fetching progress metrics:', metricsResult.reason);
        }

        if (progressionResult.status === 'fulfilled') {
          setProgressionData(progressionResult.value);
        } else {
          console.error('Error fetching progression data:', progressionResult.reason);
        }

        setIsSessionLoading(false);

        // Show error only if critical calls failed (progress + session)
        if (progressResult.status === 'rejected' && sessionResult.status === 'rejected') {
          setError('Failed to load data. Please try again later.');
        }

        const activeTrackId = sessionStorage.getItem('activeTrackId');
        if (activeTrackId && Object.keys(TRACK_NAMES).includes(activeTrackId)) {
          setExpandedTrackId(activeTrackId);
        }
      } catch (err) {
        if (!isMounted) return;
        setIsSessionLoading(false);
        if (!(err instanceof AxiosError && err.response?.status === 404)) {
          console.error('Error fetching data:', err);
          setError('Failed to load data. Please try again later.');
        }
      } finally {
        if (isMounted) {
          setComponentLoading(componentId, false);
        }
      }
    };

    initializeData();

    return () => {
      isMounted = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId, expandedTrackId, navigate, registerLoadingComponent, setComponentLoading, location.key, checkAndShowFocusTrackAlert]);

  useEffect(() => {
    if (expandedTrackId) {
      // Don't show completion alert if user's focusTrack is ALL
      if (user?.focusTrack === 'ALL') {
        setShowCompletionAlert(false);
        setTargetAchievedComplete(false); // Reset target achieved state for ALL users
      } else if (isTrackComplete(expandedTrackId)) {
        setCompletionTriggerType('facts_mastered');
        setShowCompletionAlert(true);
      } else if (targetAchievedComplete) {
        setCompletionTriggerType('cqpm_achieved');
        setShowCompletionAlert(true);
      } else {
        setShowCompletionAlert(false);
      }
    } else {
      setShowCompletionAlert(false);
    }
  }, [expandedTrackId, masteredFactsCounts, targetAchievedComplete, user?.focusTrack]);

  const handleStartClick = async () => {
    try {
      // Validate activeTrackId is set for users with ALL access
      if (user?.focusTrack === 'ALL') {
        const activeTrackId = sessionStorage.getItem('activeTrackId');
        if (!activeTrackId || !Object.keys(TRACK_NAMES).includes(activeTrackId)) {
          console.error('[Dashboard] Invalid or missing activeTrackId for ALL user');
          setError('Please select a track before starting.');
          return;
        }
      }
      
      // If session data is not loaded yet, fetch it first
      if (!sessionData || isSessionLoading) {
        setIsSessionLoading(true);

        try {
          if (user?.userId) {
            const sessionResponse = await getSession(user.userId, 'all');
            setSessionData(sessionResponse);

            // Check for fluency drill day first — overrides all other activities
            if (sessionResponse?.availableActivities?.fluencyDrill?.active) {
              navigate('/fluency-drill', {
                state: { fluencyData: sessionResponse.availableActivities.fluencyPractice }
              });
              return;
            }

            // After loading, check available activities and route appropriately
            if (sessionResponse?.availableActivities?.learn?.facts &&
                sessionResponse.availableActivities.learn.facts.length > 0) {
              navigate('/learn', { 
                state: { 
                  facts: sessionResponse.availableActivities.learn.facts
                } 
              });
              return;
            } else if (sessionResponse?.availableActivities?.accuracyPractice?.facts && 
                      sessionResponse.availableActivities.accuracyPractice.facts.length > 0) {
              navigate('/accuracy-practice', {
                state: { 
                  facts: sessionResponse.availableActivities.accuracyPractice.facts
                }
              });
              return;
            } else if (sessionResponse?.availableActivities?.fluencyPractice) {
              navigate('/fluency-practice', {
                state: {
                  fluencyData: sessionResponse.availableActivities.fluencyPractice
                }
              });
              return;
            } else {
              setError('No practice activities available at this time.');
            }
          } else {
            setError('User not logged in. Please log in again.');
          }
        } catch (err) {
          console.error('Error fetching session data on demand:', err);
          setError('Failed to load activities. Please try again.');
        } finally {
          setIsSessionLoading(false);
        }
        return;
      }
      
      // Check for fluency drill day first — overrides all other activities
      if (sessionData?.availableActivities?.fluencyDrill?.active) {
        navigate('/fluency-drill', {
          state: { fluencyData: sessionData.availableActivities.fluencyPractice }
        });
        return;
      }

      // Check available activities and route to the appropriate practice
      if (sessionData?.availableActivities?.learn?.facts && sessionData.availableActivities.learn.facts.length > 0) {
        // Priority 1: Learning facts
        navigate('/learn', { 
          state: { 
            facts: sessionData.availableActivities.learn.facts
          } 
        });
      } else if (sessionData?.availableActivities?.accuracyPractice?.facts && sessionData.availableActivities.accuracyPractice.facts.length > 0) {
        // Priority 2: Accuracy practice
        navigate('/accuracy-practice', {
          state: { 
            facts: sessionData.availableActivities.accuracyPractice.facts
          }
        });
      } else if (sessionData?.availableActivities?.fluencyPractice) {
        // Priority 3: Fluency practice
        navigate('/fluency-practice', {
          state: {
            fluencyData: sessionData.availableActivities.fluencyPractice
          }
        });
      } else {
        // No activities available
        setError('No practice activities available at this time.');
      }
    } catch (error) {
      console.error('Error in handleStartClick:', error);
      setError('Failed to load facts. Please try again.');
    }
  };

  const getTrackProgress = (trackId: string): UserProgress | undefined => {
    // Find the progress for the current track (including L2 tracks)
    return allTracks.find(track => track.trackId === trackId);
  };

  const getTrackStats = (trackId: string) => {
    const totalFacts = TRACK_LENGTHS[trackId as keyof typeof TRACK_LENGTHS] || 0;
    return { totalFacts, masteredFacts: masteredFactsCounts[trackId] || 0 };
  };

  const handleMasteredFactsCount = useCallback((count: number) => {
    if (!expandedTrackId) return;
    setMasteredFactsCounts(prev => ({
      ...prev,
      [expandedTrackId]: count
    }));
  }, [expandedTrackId]);

  const handleContinueAssessments = () => {
    // Validate activeTrackId is set for users with ALL access
    if (user?.focusTrack === 'ALL') {
      const activeTrackId = sessionStorage.getItem('activeTrackId');
      if (!activeTrackId || !Object.keys(TRACK_NAMES).includes(activeTrackId)) {
        console.error('[Dashboard] Invalid or missing activeTrackId for ALL user');
        setError('Please select a track before starting an assessment.');
        return;
      }
    }
    
    navigate('/progress-assessment', {
      state: { returnTo: 'dashboard' }
    });
  };

  const handleProgressAssessment = () => {
    // Validate activeTrackId is set for users with ALL access
    if (user?.focusTrack === 'ALL') {
      const activeTrackId = sessionStorage.getItem('activeTrackId');
      if (!activeTrackId || !Object.keys(TRACK_NAMES).includes(activeTrackId)) {
        console.error('[Dashboard] Invalid or missing activeTrackId for ALL user');
        setError('Please select a track before starting a progress assessment.');
        return;
      }
    }
    
    navigate('/progress-assessment', {
      state: { returnTo: 'dashboard' }
    });
  };

  // Helper function to calculate total practice contexts
  const getTotalPracticeContexts = () => {
    if (!expandedTrackId || !allTracks.length) return 0;
    
    const currentTrackProgress = allTracks.find(track => track.trackId === expandedTrackId);
    if (!currentTrackProgress?.facts) return 0;
    
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const todayKey = new Date().toLocaleDateString('en-CA', { timeZone: tz }); // YYYY-MM-DD in user TZ
    
    let totalContexts = 0;
    Object.values(currentTrackProgress.facts).forEach((factProgress: any) => {
      if (factProgress?.todayStats) {
        Object.entries(factProgress.todayStats).forEach(([context, stats]: [string, any]) => {
          if (!stats?.date) return;
          if (!(context.startsWith('accuracy') || context.startsWith('fluency'))) return;
          const statsKey =
            typeof stats.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(stats.date)
              ? stats.date
              : new Date(stats.date).toLocaleDateString('en-CA', { timeZone: tz });
          if (statsKey === todayKey) {
            totalContexts++;
          }
        });
      }
    });
    
    return totalContexts;
  };



  const isTrackComplete = (trackId: string): boolean => {
    const { totalFacts, masteredFacts } = getTrackStats(trackId);
    return masteredFacts === totalFacts && totalFacts > 0;
  };


  // Get target CQPM based on track
  const getTargetCQPM = (trackId?: string) => {
    // If no trackId provided, use the current track
    const currentTrackId = trackId || expandedTrackId;
    
    // If it's 'ALL' or no specific track, fall back to grade-based targets
    if (!currentTrackId || currentTrackId === 'ALL') {
      if (!user?.ageGrade) return 30;
      return CQPM_TARGETS[user.ageGrade] || 30;
    }
    
    // Get track-based target
    const targetCQPM = CQPM_TARGETS_BY_TRACK[currentTrackId as keyof typeof CQPM_TARGETS_BY_TRACK];
    if (targetCQPM !== undefined) {
      return targetCQPM;
    }
    
    // Fall back to grade-based if track not found
    if (!user?.ageGrade) return 30;
    return CQPM_TARGETS[user.ageGrade] || 30;
  };

  // Get target fluency level based on user's grade
  const getTargetFluency = () => {
    if (!user?.ageGrade) return 1.5; // Default to 1.5 seconds if grade not available
    return FLUENCY_TARGETS[user.ageGrade] || 1.5; // Default to 1.5 seconds if grade not found in targets
  };


  // Update expandedTrackId when user.focusTrack changes
  useEffect(() => {
    if (user?.focusTrack && user.focusTrack !== 'ALL' && Object.keys(TRACK_NAMES).includes(user.focusTrack)) {
      setExpandedTrackId(user.focusTrack);
      sessionStorage.setItem('activeTrackId', user.focusTrack);
    }
  }, [user?.focusTrack]);

  // Check if a track is locked based on user's focusTrack
  const isTrackLocked = (trackId: string): boolean => {
    if (!user?.focusTrack || user.focusTrack === 'ALL') return false;
    return user.focusTrack !== trackId;
  };

  // Computed button state — interactive as soon as session data arrives
  const buttonState = useMemo(() => {
    if (isSessionLoading || !sessionData) {
      return { type: 'loading', ready: false };
    }

    const determineButtonType = () => {
      // Priority 1: Weekly assessment (7+ days)
      if (sessionData.assessmentStatus?.daysSinceLastAssessment !== undefined &&
          sessionData.assessmentStatus.daysSinceLastAssessment >= 7) {
        return 'assessment';
      }

      // Priority 2: First daily assessment
      if (sessionData.dailyAssessmentCount === 0) {
        return 'assessment';
      }

      // Priority 3: All today's progress completed (updates reactively)
      if (allTodaysProgressComplete) {
        return 'assessment';
      }

      // Priority 4: Practice if activities available
      if (sessionData.availableActivities &&
          (sessionData.availableActivities.learn?.facts?.length ||
           sessionData.availableActivities.accuracyPractice?.facts?.length ||
           sessionData.availableActivities.fluencyPractice)) {
        return 'practice';
      }

      // Default: Assessment
      return 'assessment';
    };

    const buttonType = determineButtonType();
    return { type: buttonType, ready: true };
  }, [sessionData, isSessionLoading, allTodaysProgressComplete]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <AnimatePresence>
        {isLoading && <LoadingOverlay />}
      </AnimatePresence>

      {/* Focus Track Alert - Show when user first completes onboarding */}
      {showFocusTrackAlert && (
        <FocusTrackAlert
          trackName={user?.focusTrack === 'ALL' 
            ? null 
            : (expandedTrackId ? TRACK_NAMES[expandedTrackId as keyof typeof TRACK_NAMES] : null)
          }
          onContinue={handleFocusTrackAlertContinue}
          isAllTracks={user?.focusTrack === 'ALL'}
        />
      )}

      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="h-16 px-4 flex items-center justify-between border-b bg-white/50 backdrop-blur-sm relative z-50"
      >
        <div className="flex-1" />
        <h1 className="text-4xl font-bold animate-fade-in flex items-center gap-3">
          <Logo size={36} />
          <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-transparent bg-clip-text">
            AlphaNumbers
          </span>
        </h1>
        <div className="flex-1 flex justify-end">
          <UserProfileDropdown />
        </div>
      </motion.div>

      <div className="flex h-[calc(100vh-4rem)]">
        {/* Sidebar - Show when there's content to display */}
        {(
          user?.focusTrack === 'ALL' ||
          sessionData?.availableActivities
        ) && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="w-64 border-r bg-white/50 backdrop-blur-sm overflow-y-auto"
          >
            <div className="border-t"></div>

            {/* Today's Progress Section */}
            <div className="p-4">
              <TodaysProgress
                sessionData={sessionData}
                isLoading={isSessionLoading}
                allTracks={allTracks}
                expandedTrackId={expandedTrackId}
                onCompletionStatusChange={handleTodaysProgressCompletionChange}
              />
            </div>
            
            {/* Learning Tracks Section - Only show when focusTrack is ALL */}
            {user?.focusTrack === 'ALL' && (
            <>
              <div className="px-4 pb-2">
                <h2 className="text-lg font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <FiBook className="text-blue-500" />
                  Learning Tracks
                </h2>
              </div>
              <nav className="px-2 space-y-1">
                {Object.entries(TRACK_NAMES)
                  .filter(([trackId]) => ['TRACK5_L2', 'TRACK6_L2', 'TRACK7_L2', 'TRACK8_L2'].includes(trackId))
                  .map(([trackId, trackName]) => {
                  const isSelected = expandedTrackId === trackId;
                  const locked = isTrackLocked(trackId);
                  
                  return (
                    <motion.button
                      key={trackId}
                      whileHover={{ scale: locked ? 1 : 1.02 }}
                      whileTap={{ scale: locked ? 1 : 0.98 }}
                      onClick={() => {
                        if (locked) return; // Prevent selection if locked

                        // Always set the track ID in sessionStorage first to ensure it's available for API calls
                        sessionStorage.setItem('activeTrackId', trackId);

                        // Then update the expanded track
                        setExpandedTrackId(isSelected ? null : trackId);
                      }}
                      className={`w-full text-left p-3 rounded-lg transition-all duration-300
                        ${isSelected ? 'bg-blue-50 text-blue-600 shadow-sm' : 'hover:bg-gray-50'}
                        ${locked ? 'opacity-60 cursor-not-allowed' : ''}`}
                      title={locked ? "This track is locked." : ""}
                    >
                      <div className="font-medium flex items-center justify-between">
                        <span>{trackName}</span>
                        {locked && <FiLock className="text-gray-500" size={16} />}
                      </div>
                    </motion.button>
                  );
                })}
              </nav>
            </>
          )}
          </motion.div>
        )}

        {/* Main content */}
        <div className="flex-1 overflow-y-auto">
          <div className="h-full flex items-start">
            <AnimatePresence mode="wait">
              {expandedTrackId ? (
                <motion.div 
                  key={expandedTrackId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="w-full max-w-4xl mx-auto p-4 md:p-6"
                >
                  {error && (
                    <motion.div 
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-lg flex items-center justify-between"
                    >
                      <span>{error}</span>
                      <button 
                        onClick={() => setError(null)}
                        className="ml-2 text-red-800 hover:text-red-900 p-1 rounded-full hover:bg-red-100 transition-colors"
                      >
                        ×
                      </button>
                    </motion.div>
                  )}
                  {(() => {
                    if (isTrackComplete(expandedTrackId) && user?.focusTrack !== 'ALL') {
                      return (
                        <>
                          <div className="w-[230px] mx-auto">
                            <div className="space-y-4">
                              {/* Course Progression */}
                              <Progression
                                userId={user?.userId || ''}
                                isActive={true}
                                preloadedData={progressionData}
                              />
                              {/* Always show ProgressMetrics */}
                              <ProgressMetrics 
                                userId={user?.userId || ''}
                                targetCQPM={getTargetCQPM()}
                                isActive={hasCompletedAssessment}
                                preloadedData={progressMetricsData}
                                onTargetAchieved={handleTargetAchieved}
                              />
                            </div>
                          </div>
                          {showCompletionAlert && (
                            <TrackCompletionAlert
                              trackName={TRACK_NAMES[expandedTrackId as keyof typeof TRACK_NAMES]}
                              onContinue={markCompletionAlertAsShown}
                              currentTrackId={expandedTrackId}
                              triggerType={completionTriggerType}
                            />
                          )}
                        </>
                      );
                    }

                    return (
                      <div className="flex flex-col lg:flex-row gap-6">
                        <div className="flex-1">
                          <div className="space-y-6">
                            <TrackProgress
                              onStartClick={handleStartClick}
                              onMasteredFactsCount={handleMasteredFactsCount}
                              trackId={expandedTrackId!}
                              targetFluency={getTargetFluency()}
                              preloadedProgress={allTracks.find(track => track.trackId === expandedTrackId) || null}
                            />
                            
                            {/* Smart Button(s) */}
                            <div className="flex gap-3">
                              {/* Show both buttons when practice contexts >= 100 AND button state is ready */}
                              {getTotalPracticeContexts() >= 100 && buttonState.ready ? (
                                <>
                                  {/* Practice Button */}
                                  <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={handleStartClick}
                                    disabled={!buttonState.ready}
                                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg shadow-md transition-all duration-300 bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-lg"
                                  >
                                    <FiPlay className="text-white" size={20} />
                                    <span className="font-medium">Continue Practice</span>
                                  </motion.button>
                                  {/* Assessment Button */}
                                  <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={handleContinueAssessments}
                                    disabled={!buttonState.ready}
                                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg shadow-md transition-all duration-300 bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:shadow-lg"
                                  >
                                    <FiZap className="text-white" size={20} />
                                    <span className="font-medium">Take an Assessment</span>
                                  </motion.button>
                                </>
                              ) : (
                                /* Single Smart Button - uses computed buttonState */
                                <motion.button
                                  whileHover={{ scale: buttonState.ready ? 1.02 : 1 }}
                                  whileTap={{ scale: buttonState.ready ? 0.98 : 1 }}
                                  onClick={() => {
                                    if (!buttonState.ready) return;
                                    const isAssessment = buttonState.type === 'assessment';
                                    isAssessment ? handleContinueAssessments() : handleStartClick();
                                  }}
                                  disabled={!buttonState.ready}
                                  className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg shadow-md transition-all duration-300 ${
                                    !buttonState.ready 
                                      ? 'bg-gray-400 cursor-not-allowed'
                                      : buttonState.type === 'assessment' 
                                        ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:shadow-lg'
                                        : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-lg'
                                  }`}
                                >
                                  {!buttonState.ready ? (
                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                  ) : buttonState.type === 'assessment' ? (
                                    <FiZap className="text-white" size={20} />
                                  ) : (
                                    <FiPlay className="text-white" size={20} />
                                  )}
                                  <span className="font-medium">
                                    {!buttonState.ready ? "Loading..." : buttonState.type === 'assessment' ? "Take an Assessment" : "Continue Practice"}
                                  </span>
                                </motion.button>
                              )}
                            </div>
                          </div>
                        </div>
                        {/* Always show ProgressMetrics and Progression */}
                        <div className="w-full lg:w-[230px]">
                          {isSessionLoading ? (
                            <div className="bg-white p-4 rounded-lg shadow-md animate-pulse">
                              <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
                              <div className="h-4 bg-gray-200 rounded w-full mb-3"></div>
                              <div className="h-4 bg-gray-200 rounded w-5/6 mb-3"></div>
                              <div className="h-10 bg-gray-200 rounded w-full mt-4"></div>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              {/* Course Progression */}
                              <Progression
                                userId={user?.userId || ''}
                                isActive={true}
                                preloadedData={progressionData}
                              />
                              {/* Always show ProgressMetrics */}
                              <ProgressMetrics
                                userId={user?.userId || ''}
                                targetCQPM={getTargetCQPM()}
                                isActive={hasCompletedAssessment}
                                preloadedData={progressMetricsData}
                                onTargetAchieved={handleTargetAchieved}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                  {showCompletionAlert && (
                    <TrackCompletionAlert
                      trackName={TRACK_NAMES[expandedTrackId as keyof typeof TRACK_NAMES]}
                      onContinue={markCompletionAlertAsShown}
                      currentTrackId={expandedTrackId}
                      triggerType={completionTriggerType}
                    />
                  )}
                </motion.div>
              ) : (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="w-full h-full flex flex-col items-center justify-center text-gray-500 gap-4"
                >
                  <FiBook size={48} className="text-gray-400" />
                  <p className="text-lg">Select a track to view progress</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

const Dashboard: React.FC = () => {
  return (
    <LoadingProvider>
      <DashboardContent />
    </LoadingProvider>
  );
};

export default Dashboard;